import '@skyscanner-internal/backpack-web/bpk-stylesheets';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';
import { afterDomContentLoaded } from 'saddlebag-browser';
import logger from 'saddlebag-logger';
import observer from 'saddlebag-observer';
import { initTrackingPreferences } from 'saddlebag-tracking-preferences';

import './public-path';
import './main.scss';
import { namespace } from './constants';

const KETCH_OBSERVER_TIMEOUT_MS = 1_500;
const KETCH_INIT_CHECK_DELAY_MS = 3_000;

const afterPageLoad = async () => {
  observer.publish('mixpanel-track', {
    name: `${namespace} Loaded`,
  });
};
(async () => {
  afterDomContentLoaded(async () => {
    try {
      // eslint-disable-next-line no-underscore-dangle
      const { props } = window.__internal || {};
      const {
        enableConsentIdService = false,
        enableCookieSizeTelemetry = false,
        enableCookieVersionRollback = false,
        enableKetchCMP = false,
        enableKetchPsidSync = false,
        enableLegacyConsentSync = false,
        enablePurposeScopedIdExistingGDPRUsers = false,
        enablePurposeScopedIdNonRegulatedMarkets = false,
        enablePurposeScopedIdService = false,
        ketchConsentCookieName = '_swb_consent_',
        ketchScriptUrl = '',
        ketchTCFStorageKey = '_ketch_tcf_tcdata_v1_',
        purposeScopedIdServiceKetchCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh',
        purposeScopedIdServiceLegacyCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh-legacy',
      } = props || {};

      initTrackingPreferences({
        enableConsentIdService,
        enableCookieSizeTelemetry,
        enableCookieVersionRollback,
        enableKetchCMP,
        enableKetchPsidSync,
        enableLegacyConsentSync,
        enablePurposeScopedIdExistingGDPRUsers,
        enablePurposeScopedIdNonRegulatedMarkets,
        consumer: 'lyrebird',
        market: props?.market,
        ketchScriptUrl,
        ketchConsentCookieName,
        ketchTCFStorageKey,
        enablePurposeScopedIdService,
        purposeScopedIdServiceKetchCmpUrl,
        purposeScopedIdServiceLegacyCmpUrl,
      });

      if (enableKetchCMP) {
        const isKetchScript = (node) => node?.nodeName === 'SCRIPT' && node.src === ketchScriptUrl;

        let ketchScriptInjected = Array.from(document.scripts).some(isKetchScript);

        const onError = (event) => {
          if (!event.filename && event.message === 'Script error.') {
            logger.logWarn({
              component: namespace,
              message: 'script_error_opaque',
              ketchScriptInjected: String(ketchScriptInjected),
              ketchInitialised: String(!!window.ketch),
            });
          }
        };
        window.addEventListener('error', onError);

        let ketchObserverTimeoutId;
        const ketchObserver = new MutationObserver((mutations) => {
          for (const { addedNodes } of mutations) {
            for (const node of addedNodes) {
              if (isKetchScript(node)) {
                ketchScriptInjected = true;
                clearTimeout(ketchObserverTimeoutId);
                ketchObserver.disconnect();
                window.removeEventListener('error', onError);
              }
            }
          }
        });
        ketchObserver.observe(document.documentElement, { childList: true, subtree: true });
        ketchObserverTimeoutId = setTimeout(() => {
          ketchObserver.disconnect();
          window.removeEventListener('error', onError);
        }, KETCH_OBSERVER_TIMEOUT_MS);

        setTimeout(() => {
          if (!window.ketch) {
            logger.logWarn({
              component: namespace,
              message: 'ketch_sdk_not_initialised',
              ketchScriptUrl,
            });
          }
        }, KETCH_INIT_CHECK_DELAY_MS);
      }

      const { default: hydrate } = await import('./hydrate');
      hydrate();
      afterPageLoad();
    } catch (err) {
      logger.logError(err, {
        component: namespace,
      });
    }
  });
})();
